import {
  ArrowsOutSimple,
  ContentArea,
  DownloadSimpleIcon,
  GridPresetMainContent,
  GridPresetSidebar,
  GridPresetWideContent,
  GridRow,
  HeroBasic,
  ImageOrVideoFullWidthBlock,
  Link,
  OrganizationOverviewGroup,
} from "/App.Components";
import "./OrganizationOverviewPage.scss";
import { OrganizationOverviewPageProps } from "/Models/Generated/OrganizationOverviewPageProps";
import { ContentAreaType } from "/Models/Generated/ContentAreaType";
import { createSpreadableBem } from "/Helpers/createBem";
import { useModal } from "/Components/Shared/ModalProvider/ModalProvider";
import { useRef } from "react";

const OrganizationOverviewPage = ({
  pageHero,
  organizationChart,
  organizationChartImageHref,
  expandImageLinkText,
  downloadImageLinkText,
  showOrganizationChartBelowMainContent,
  group1,
  group2,
  group3,
  sidebar,
}: OrganizationOverviewPageProps) => {
  const groups = [group1, group2, group3].filter((i) => !!i);
  const bem = createSpreadableBem("organization-overview-page-norad");
  const { openModal } = useModal();
  const organizationChartRef = useRef<HTMLDivElement>(null);
  const imageHref = organizationChartImageHref;
  const showOrganizationChart = !!organizationChart && !!imageHref;

  const renderOrganizationChart = () => {
    if (!organizationChart || !imageHref) {
      return null;
    }

    return (
      <GridRow>
        <GridPresetWideContent>
          <div {...bem("__organization-chart")} ref={organizationChartRef}>
            <ImageOrVideoFullWidthBlock
              {...organizationChart}
              showZoomButton={false}
            />
          </div>
        </GridPresetWideContent>
      </GridRow>
    );
  };

  const renderOrganizationChartLinks = () => {
    if (!organizationChart || !imageHref) {
      return null;
    }

    return (
      <GridRow>
        <GridPresetWideContent>
          <div {...bem("__organization-chart-links")}>
            {expandImageLinkText && (
              <button
                {...bem("__expand-link")}
                aria-label={expandImageLinkText}
                type="button"
                onClick={() =>
                  openModal(
                    <ImageOrVideoFullWidthBlock
                      {...organizationChart}
                      insideModal
                      source={organizationChartRef}
                      showZoomButton={false}
                    />
                  )
                }
              >
                <span>{expandImageLinkText}</span>
                <ArrowsOutSimple size="20" thickness="Reg" />
              </button>
            )}
            {downloadImageLinkText && (
              <Link
                href={imageHref}
                text={downloadImageLinkText}
                isExternal={false}
                icon={DownloadSimpleIcon}
                iconPlacement="after"
                size="small"
                download
              />
            )}
          </div>
        </GridPresetWideContent>
      </GridRow>
    );
  };

  return (
    <div {...bem("")}>
      {pageHero && <HeroBasic {...pageHero} />}
      {showOrganizationChart && !showOrganizationChartBelowMainContent && (
        <div {...bem("__organizational-overview")}>
          {renderOrganizationChart()}
          {renderOrganizationChartLinks()}
        </div>
      )}
      <div {...bem("__main-content")}>
        {(groups?.length ?? 0) > 0 && (
          <GridRow>
            <GridPresetMainContent>
              {groups.map((group, idx) => (
                <OrganizationOverviewGroup
                  {...group}
                  key={idx}
                  id={idx.toString()}
                  isFirst={idx === 0}
                  isLast={idx + 1 === groups.length}
                />
              ))}
            </GridPresetMainContent>
            {(sidebar?.propertyName || ContentArea.HasContent(sidebar)) && (
              <GridPresetSidebar>
                <aside>
                  <ContentArea
                    {...sidebar}
                    contentAreaType={ContentAreaType.OneThird}
                    margin={ContentArea.ItemMargin.Sidebar}
                  />
                </aside>
              </GridPresetSidebar>
            )}
          </GridRow>
        )}
      </div>
      {showOrganizationChart && showOrganizationChartBelowMainContent && (
        <div {...bem("__organizational-overview", "below")}>
          {renderOrganizationChart()}
          {renderOrganizationChartLinks()}
        </div>
      )}
    </div>
  );
};

export default OrganizationOverviewPage;
